import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface CheckboxRowOption {
    value: string;
    label: string;
}

export interface CheckboxRowGroupProps {
    options: CheckboxRowOption[];
    values: string[];
    onChange: (values: string[]) => void;
    /** Accessible name for the group. */
    label: string;
    /** Refuses further selections past this many. Unlimited when omitted. */
    max?: number;
    className?: string;
}

/**
 * The stacked multi-select answer: a label and a checkbox, one per row.
 *
 * Native checkbox semantics, so each row is its own tab stop and Space toggles
 * it — unlike the single-select group, where the whole set is one choice and
 * therefore one stop.
 */
export const CheckboxRowGroup = ({
    options,
    values,
    onChange,
    label,
    max,
    className,
}: CheckboxRowGroupProps) => {
    const toggle = (value: string) => {
        if (values.includes(value)) {
            onChange(values.filter((entry) => entry !== value));
            return;
        }
        if (max !== undefined && values.length >= max) return;
        onChange([...values, value]);
    };

    return (
        <div
            aria-label={label}
            className={cn('flex flex-col gap-2', className)}
            role="group"
        >
            {options.map((option) => {
                const checked = values.includes(option.value);
                const atLimit =
                    !checked && max !== undefined && values.length >= max;

                return (
                    <button
                        aria-checked={checked}
                        aria-disabled={atLimit || undefined}
                        className={cn(
                            'flex h-13.25 w-full items-center justify-between gap-4 rounded-3 px-4.25 text-left transition-colors',
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                            QUESTIONNAIRE_COLOUR.surface,
                            atLimit
                                ? 'cursor-not-allowed opacity-50'
                                : QUESTIONNAIRE_COLOUR.surfaceHover,
                        )}
                        key={option.value}
                        onClick={() => toggle(option.value)}
                        role="checkbox"
                        type="button"
                    >
                        <span
                            className={cn(
                                'min-w-0 truncate font-sans text-4 leading-[1.3] tracking-[-0.01em]',
                                QUESTIONNAIRE_COLOUR.ink,
                            )}
                        >
                            {option.label}
                        </span>
                        <span
                            className={cn(
                                'flex size-5 shrink-0 items-center justify-center rounded-1 border transition-colors',
                                checked
                                    ? 'border-[#181818] bg-[#181818] text-white'
                                    : 'border-[#c7c7c7] bg-white',
                            )}
                        >
                            {checked ? (
                                <Check
                                    aria-hidden="true"
                                    className="size-3.5"
                                />
                            ) : null}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
